import { type JSX, type KeyboardEvent, useRef, useState } from 'react';

import {
  appScreens,
  dayPlan,
  downloadSlots,
  exploreSlots,
  growSlots,
  newsFeatured,
  newsSlots,
  osTabs,
  portalSlots,
  solutionSlots,
} from '../content/homepage-content';
import { useHomepageContent } from '../content/homepage-model';
import { useContentOverride } from '../content/ContentProvider';
import { routes } from '../routes';
import { useT } from '../i18n';
import {
  Reveal,
  useAutoAdvance,
  useCarousel,
  useHeroScrollProgress,
  useNearViewport,
  useParallax,
} from './motion';
import { CmsAction, DestinationLink, JTArrow, PageContainer } from './primitives';
import { PharmacyDeliveryPreview } from './PharmacyDeliveryPreview';

/**
 * The homepage sections, in the order the 2026-10 mockup sets out.
 *
 * Each is layout over two sources: the editable content blocks (every word,
 * link, picture, position and on/off switch, read through `useHomepageContent`)
 * and `content/homepage-content`, which says how each slot is drawn. None of
 * them contains domain logic, and none fabricates a backend result.
 */

/** Left/right arrow keys on a focused carousel, as the arrows would. */
function arrowKeys(step: (direction: -1 | 1) => void) {
  return (event: KeyboardEvent<HTMLElement>): void => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    }
  };
}

/** Whether this sticky panel slides over a panel above it. */
interface StackProps {
  /** False when every panel it would overlap is switched off. */
  readonly overlaps?: boolean;
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

export function JTHero(): JSX.Element {
  const c = useHomepageContent();
  const uploaded = c.image('hero.image', '');
  const progress = useHeroScrollProgress(380);
  const alt = 'A couple walking past a Just Treated pharmacy, with a branded delivery car at the kerb';
  const zoom = { transform: `scale(${(1 + 0.28 * progress).toFixed(3)})` };

  return (
    <>
      <section className="jt-hero" aria-labelledby="jt-hero-title">
        <Reveal as="h1" index={0} className="jt-hero__title">
          <span id="jt-hero-title">{c.text('hero.title')}</span>
        </Reveal>
        <Reveal as="p" index={1} className="jt-hero__lede">
          {c.text('hero.lede')}
        </Reveal>
        <Reveal index={2} className="jt-hero__actions">
          <CmsAction
            href={c.href('hero.cta-primary-link')}
            className="jt-button jt-button--ink jt-button--pill"
            analyticsKey="hero_get_started"
          >
            {c.text('hero.cta-primary')}
          </CmsAction>
          <CmsAction
            href={c.href('hero.cta-secondary-link')}
            className="jt-button jt-button--lavender jt-button--pill"
            analyticsKey="hero_partner"
          >
            {c.text('hero.cta-secondary')}
          </CmsAction>
        </Reveal>
      </section>

      {/*
        The band grows upward over the headline and zooms as you scroll into
        it: over the first 380px it rises 230px and scales from 1 to 1.28.
      */}
      <div
        className="jt-hero__band"
        style={{
          marginTop: `${(-230 * progress).toFixed(1)}px`,
          height: `${(560 + 230 * progress).toFixed(1)}px`,
        }}
      >
        {/*
          An uploaded hero replaces the shipped one. The shipped photograph
          stays the default: it is the largest contentful paint, it is
          preloaded from index.html, and it ships pre-encoded at two widths.
        */}
        {uploaded.src !== '' ? (
          <img
            className="jt-hero__image"
            src={uploaded.src}
            alt={alt}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={zoom}
          />
        ) : (
          <picture>
            <source type="image/webp" srcSet="/hero-1600.webp 1600w, /hero-2400.webp 2400w" sizes="100vw" />
            <img
              className="jt-hero__image"
              src="/hero-1600.jpg"
              alt={alt}
              width={1774}
              height={887}
              // The hero is the largest contentful paint; it must not be lazy.
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={zoom}
            />
          </picture>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Explore Just Treated
// ---------------------------------------------------------------------------

export function JTServicesSection(): JSX.Element {
  const c = useHomepageContent();
  const trackRef = useRef<HTMLUListElement | null>(null);
  const carousel = useCarousel(trackRef, { autoStep: 390, manualStep: 400 });
  const slots = c.ordered(exploreSlots, (slot) => `explore.card${slot.n}-order`);

  return (
    <section className="jt-section jt-explore" aria-labelledby="jt-explore-title">
      <div className="jt-explore__head">
        <Reveal as="h2" className="jt-h2">
          <span id="jt-explore-title">{c.text('explore.title')}</span>
        </Reveal>
        <p className="jt-lede">{c.text('explore.lede')}</p>
      </div>

      <ul
        ref={trackRef}
        className="jt-explore__track"
        tabIndex={0}
        role="group"
        aria-label="Explore Just Treated services"
        onKeyDown={arrowKeys(carousel.step)}
        {...carousel.hold}
      >
        {slots.map((slot, index) => {
          const k = `explore.card${slot.n}`;
          const title = c.text(`${k}-title`);
          const picture = c.image(slot.imageKey, slot.image);
          return (
            <Reveal
              as="li"
              key={slot.n}
              index={index}
              step={70}
              className={`jt-scard jt-scard--${slot.tone} jt-scard--${slot.media}`}
            >
              {slot.media !== 'phone' ? (
                <img className="jt-scard__photo" src={picture.src} alt="" loading="lazy" decoding="async" />
              ) : null}
              <h3 className="jt-scard__title">{title}</h3>
              <p className="jt-scard__copy">{c.text(`${k}-copy`)}</p>
              {/*
                Drawn as the design's button but deliberately not a link (owner,
                2026-10-06): the destinations behind these cards are not built
                yet. The link blocks stay in the dashboard, unrendered, so the
                buttons can become links again without a schema change.
              */}
              <span className={`jt-scard__cta jt-scard__cta--${slot.button}`}>{c.text(`${k}-cta`)}</span>
              {slot.media === 'phone' ? (
                <span className="jt-scard__phone">
                  <span className="jt-scard__key jt-scard__key--a" aria-hidden="true" />
                  <span className="jt-scard__key jt-scard__key--b" aria-hidden="true" />
                  <span className="jt-scard__key jt-scard__key--c" aria-hidden="true" />
                  <span className="jt-scard__bezel">
                    <span className="jt-scard__screen">
                      <span className="jt-scard__island" aria-hidden="true" />
                      <img
                        className="jt-scard__shot"
                        src={picture.src}
                        alt={`${title} in the Just Treated app`}
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                  </span>
                </span>
              ) : null}
            </Reveal>
          );
        })}
      </ul>

      <div className="jt-explore__arrows">
        <JTArrow
          direction="prev"
          label="Previous services"
          atEnd={carousel.atStart}
          onClick={() => carousel.step(-1)}
        />
        <JTArrow
          direction="next"
          label="More services"
          atEnd={carousel.atEnd}
          onClick={() => carousel.step(1)}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pharmacy OS
// ---------------------------------------------------------------------------

export function JTPharmacyOsSection(): JSX.Element {
  const c = useHomepageContent();
  const tabs = c.ordered(osTabs, (tab) => `os.${tab.slug}.order`);
  const { index, cycle, select } = useAutoAdvance(tabs.length, 4000);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const near = useNearViewport(stageRef, '600px');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (next: number): void => {
    const target = (next + tabs.length) % tabs.length;
    select(target);
    tabRefs.current[target]?.focus();
  };

  return (
    <div className="jt-sticky jt-sticky--os">
      <section className="jt-os" aria-labelledby="jt-os-title">
        <Reveal as="h2" className="jt-h2 jt-h2--onDark">
          <span id="jt-os-title">{c.text('os.title')}</span>
        </Reveal>
        <p className="jt-lede jt-lede--onDark">{c.text('os.lede')}</p>

        {tabs.length > 0 ? (
          <>
            <div className="jt-os__tabs" role="tablist" aria-label="Pharmacy OS features">
              {tabs.map((tab, i) => (
                <button
                  key={tab.slug}
                  ref={(node) => {
                    tabRefs.current[i] = node;
                  }}
                  id={`jt-os-tab-${tab.slug}`}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-controls={`jt-os-panel-${tab.slug}`}
                  tabIndex={i === index ? 0 : -1}
                  className={`jt-os__tab ${i === index ? 'jt-os__tab--active' : ''}`}
                  onClick={() => select(i)}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowRight') {
                      event.preventDefault();
                      move(i + 1);
                    }
                    if (event.key === 'ArrowLeft') {
                      event.preventDefault();
                      move(i - 1);
                    }
                  }}
                >
                  {/* The dwell, drawn as a fill that restarts with each tab. */}
                  {i === index ? (
                    <span key={`${index}-${cycle}`} className="jt-os__fill" aria-hidden="true" />
                  ) : null}
                  <span className="jt-os__tablabel">{c.text(`os.${tab.slug}.label`)}</span>
                </button>
              ))}
            </div>

            <Reveal variant="rise" className="jt-os__stage">
              <div ref={stageRef}>
                <div className="jt-os__track" style={{ ['--os-i' as string]: index }}>
                  {tabs.map((tab, i) => {
                    const shot = c.image(`os.${tab.slug}.image`, tab.image);
                    return (
                      <div
                        key={tab.slug}
                        id={`jt-os-panel-${tab.slug}`}
                        role="tabpanel"
                        aria-labelledby={`jt-os-tab-${tab.slug}`}
                        aria-hidden={i !== index}
                        className={`jt-os__screen ${i === index ? 'jt-os__screen--active' : ''}`}
                      >
                        {tab.live === true && !shot.uploaded ? (
                          <PharmacyDeliveryPreview fallbackSrc={tab.image} alt={tab.alt} live={near} />
                        ) : (
                          <img
                            className="jt-os__shot"
                            src={shot.src}
                            alt={tab.alt}
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        <span className="jt-os__glow" aria-hidden="true" />
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </>
        ) : null}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Solutions for every business
// ---------------------------------------------------------------------------

export function JTSolutionsSection({ overlaps = true }: StackProps): JSX.Element {
  const c = useHomepageContent();
  const trackRef = useRef<HTMLUListElement | null>(null);
  const carousel = useCarousel(trackRef, { autoStep: 388, manualStep: 1170 });
  const cards = c.ordered(solutionSlots, (slot) => `solutions.${slot.slug}.order`);

  return (
    <div className={`jt-sticky jt-sticky--solutions ${overlaps ? '' : 'jt-sticky--lead'}`}>
      <section className="jt-solutions" aria-labelledby="jt-solutions-title">
        <div className="jt-solutions__inner">
          <Reveal className="jt-solutions__head">
            <h2 className="jt-h2" id="jt-solutions-title">
              {c.text('solutions.title')}
            </h2>
            <p className="jt-lede jt-lede--start">{c.text('solutions.lede')}</p>
            <div className="jt-solutions__actions">
              <CmsAction
                href={c.href('solutions.link')}
                className="jt-textlink"
                analyticsKey="solutions_explore"
              >
                {c.text('solutions.cta')} <span aria-hidden="true">→</span>
              </CmsAction>
              <div className="jt-solutions__arrows">
                {/* The arrow that can lead is the filled one; at the end they swap. */}
                <JTArrow
                  direction="prev"
                  label="Previous solutions"
                  tone={carousel.atEnd ? 'solid' : 'quiet'}
                  atEnd={carousel.atStart}
                  size={20}
                  onClick={() => carousel.step(-1)}
                />
                <JTArrow
                  direction="next"
                  label="More solutions"
                  tone={carousel.atEnd ? 'quiet' : 'solid'}
                  atEnd={carousel.atEnd}
                  size={20}
                  onClick={() => carousel.step(1)}
                />
              </div>
            </div>
          </Reveal>

          <Reveal>
            <ul
              ref={trackRef}
              className="jt-solutions__track"
              tabIndex={0}
              role="group"
              aria-label="Business solutions"
              onKeyDown={arrowKeys(carousel.step)}
              {...carousel.hold}
            >
              {cards.map((slot) => (
                <li key={slot.slug} className="jt-solcard">
                  <img
                    className="jt-solcard__photo"
                    src={c.image(`solutions.${slot.slug}.image`, slot.image).src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    style={{ objectPosition: slot.focus }}
                  />
                  <h3 className="jt-solcard__title">{c.text(`solutions.${slot.slug}.title`)}</h3>
                  <p className="jt-solcard__copy">{c.text(`solutions.${slot.slug}.copy`)}</p>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Partner Portal
// ---------------------------------------------------------------------------

export function JTPartnerPortalSection({ overlaps = true }: StackProps): JSX.Element {
  const c = useHomepageContent();
  const trackRef = useRef<HTMLUListElement | null>(null);
  // One card per step, measured, because the card width follows the viewport.
  const oneCard = (track: HTMLElement): number =>
    (track.firstElementChild?.getBoundingClientRect().width ?? 282) + 18;
  const carousel = useCarousel(trackRef, { autoStep: oneCard });
  const cards = c.ordered(portalSlots, (slot) => `portal.${slot.slug}.order`);

  return (
    <div className={`jt-sticky jt-sticky--portal ${overlaps ? '' : 'jt-sticky--lead'}`}>
      <section className="jt-portal" aria-labelledby="jt-portal-title">
        <div className="jt-portal__inner">
          <Reveal className="jt-portal__head">
            <span className="jt-eyebrow">{c.text('portal.eyebrow')}</span>
            <h2 className="jt-h2" id="jt-portal-title">
              {c.text('portal.title')}
            </h2>
            <p className="jt-lede">{c.text('portal.lede')}</p>
          </Reveal>

          <Reveal>
            <ul
              ref={trackRef}
              className="jt-portal__grid"
              tabIndex={0}
              role="group"
              aria-label="Partner Portal insights"
              onKeyDown={arrowKeys(carousel.step)}
              {...carousel.hold}
            >
              {cards.map((slot) => {
                const title = c.text(`portal.${slot.slug}.title`);
                return (
                  <li key={slot.slug} className="jt-pcard">
                    <h3 className="jt-pcard__title">{title}</h3>
                    <p className="jt-pcard__copy">{c.text(`portal.${slot.slug}.copy`)}</p>
                    <span className="jt-pcard__media">
                      <img
                        src={c.image(`portal.${slot.slug}.image`, slot.image).src}
                        alt={`${title} in the Partner Portal`}
                        loading="lazy"
                        decoding="async"
                        style={{ objectPosition: slot.focus }}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          </Reveal>

          <div className="jt-portal__arrows">
            <JTArrow
              direction="prev"
              label="Previous insights"
              tone="ring"
              size={17}
              atEnd={carousel.atStart}
              onClick={() => carousel.step(-1)}
            />
            <JTArrow
              direction="next"
              label="More insights"
              tone="ring"
              size={17}
              atEnd={carousel.atEnd}
              onClick={() => carousel.step(1)}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The super-app statement
// ---------------------------------------------------------------------------

const DAY_ICONS: Readonly<Record<string, JSX.Element>> = {
  pill: (
    <g>
      <rect x="3.4" y="9" width="17.2" height="6.4" rx="3.2" transform="rotate(-45 12 12)" />
      <path d="M9.6 6.9 L17.1 14.4" />
    </g>
  ),
  tooth: (
    <path d="M7.6 4.2c1.6 0 2.6.9 4.4.9s2.8-.9 4.4-.9c2.6 0 3.8 2.2 3.8 4.6 0 2.8-1.4 4.4-1.9 6.9-.5 2.6-.9 5.2-2.5 5.2-1.7 0-1.6-4.6-3.8-4.6s-2.1 4.6-3.8 4.6c-1.6 0-2-2.6-2.5-5.2-.5-2.5-1.9-4.1-1.9-6.9 0-2.4 1.2-4.6 3.8-4.6Z" />
  ),
  bowl: (
    <g>
      <path d="M3.6 11.4h16.8a8.4 8.4 0 0 1-16.8 0Z" />
      <path d="M12 11.4c0-3 1.6-5.6 4.4-6.6M9 11.4c-.4-2.2.4-3.8 2-4.6" />
    </g>
  ),
  dumbbell: <path d="M6.4 8v8M17.6 8v8M3.6 10v4M20.4 10v4M6.4 12h11.2" />,
  gift: (
    <g>
      <rect x="4" y="9" width="16" height="11" rx="1.6" />
      <path d="M3 9h18M12 9v11" />
      <path d="M12 9c-1.4-3.6-5.4-4.4-5.4-1.8 0 1.4 2.2 1.8 5.4 1.8Zm0 0c1.4-3.6 5.4-4.4 5.4-1.8 0 1.4-2.2 1.8-5.4 1.8Z" />
    </g>
  ),
};

function Glyph({ children, size = 15 }: { readonly children: JSX.Element; readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** The design's full "Your day" screen. Demonstration content throughout. */
function YourDayScreen(): JSX.Element {
  return (
    <span className="jt-day">
      <span className="jt-day__bar">
        <span className="jt-day__logo">JT</span>
        <span className="jt-day__where">
          <span className="jt-day__kicker">YOUR HEALTH, LOCAL CARE</span>
          <span className="jt-day__place">{dayPlan.place} ▾</span>
        </span>
        <span className="jt-day__round">
          <Glyph>
            <g>
              <path d="M12 3.6a5.6 5.6 0 0 0-5.6 5.6v3.6L4.8 16.4h14.4L17.6 12.8V9.2A5.6 5.6 0 0 0 12 3.6Z" />
              <path d="M10 18.8a2.1 2.1 0 0 0 4 0" />
            </g>
          </Glyph>
          <span className="jt-day__ping" />
        </span>
        <span className="jt-day__round">
          <Glyph>
            <g>
              <path d="M3.4 8.4h17.2L18.6 19.4H5.4L3.4 8.4Z" />
              <path d="M8.4 8.4 10.2 4M15.6 8.4 13.8 4" />
            </g>
          </Glyph>
          <span className="jt-day__count">3</span>
        </span>
      </span>
      <span className="jt-day__sheet">
        <span className="jt-day__head">
          <span className="jt-day__titles">
            <span className="jt-day__title">Your day</span>
            <span className="jt-day__subtitle">Everything for today, in one glance.</span>
          </span>
          <span className="jt-day__today">Today ▾</span>
        </span>
        <span className="jt-day__week">
          {dayPlan.week.map(([day, date], i) => (
            <span key={day} className={`jt-day__date ${i === 0 ? 'jt-day__date--on' : ''}`}>
              <span className="jt-day__dow">{day}</span>
              <span className="jt-day__dom">{date}</span>
            </span>
          ))}
        </span>
        <span className="jt-day__timeline">
          <span className="jt-day__rail" />
          {dayPlan.items.map((item) => (
            <span key={item.name} className={`jt-day__item jt-day__item--${item.tone}`}>
              <span className="jt-day__time">
                {item.time}
                <span className="jt-day__half">{item.half}</span>
              </span>
              <span className="jt-day__dot" />
              <span className="jt-day__card">
                <span className="jt-day__tile">
                  <Glyph size={19}>{DAY_ICONS[item.icon] ?? <g />}</Glyph>
                </span>
                <span className="jt-day__text">
                  <span className="jt-day__name">{item.name}</span>
                  <span className="jt-day__meta">{item.meta}</span>
                </span>
                <span className="jt-day__chev">
                  <Glyph size={13}>
                    <path d="M9.4 5.4 16 12l-6.6 6.6" />
                  </Glyph>
                </span>
              </span>
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}

export function JTSuperAppSection({ overlaps = true }: StackProps): JSX.Element {
  const c = useHomepageContent();
  const { index, select } = useAutoAdvance(appScreens.length, 6000);
  const active = appScreens[index] ?? appScreens[0];

  if (active === undefined) return <></>;

  return (
    <section
      className={`jt-superapp ${overlaps ? '' : 'jt-superapp--lead'}`}
      aria-labelledby="jt-superapp-title"
    >
      <span className="jt-eyebrow jt-eyebrow--onViolet">{c.text('superapp.eyebrow')}</span>
      <Reveal as="h2" className="jt-h2 jt-h2--onDark jt-superapp__title">
        <span id="jt-superapp-title">{c.text('superapp.title')}</span>
      </Reveal>
      <p className="jt-lede jt-lede--onDark">{c.text('superapp.lede')}</p>
      <CmsAction
        href={c.href('superapp.link')}
        className="jt-button jt-button--ink jt-button--pill jt-superapp__cta"
        analyticsKey="superapp_mission"
      >
        {c.text('superapp.cta')}
      </CmsAction>

      <Reveal variant="rise" className="jt-superapp__stage">
        <div className="jt-superapp__caption" aria-live="polite">
          <span className="jt-superapp__feature">{active.featureName}</span>
          <span className="jt-superapp__featurecopy">{active.featureCopy}</span>
        </div>

        <div className="jt-superapp__track" style={{ ['--app-i' as string]: index }}>
          {appScreens.map((screen, i) => (
            <button
              key={screen.key}
              type="button"
              className="jt-phone"
              style={{ opacity: i === index ? 1 : 0.45, transform: `scale(${i === index ? 1 : 0.88})` }}
              onClick={() => select(i)}
              aria-label={`Show ${screen.featureName}`}
              tabIndex={i === index ? 0 : -1}
            >
              <span className="jt-phone__shell">
                {screen.key === 'your-day' ? (
                  <span className="jt-phone__screen jt-phone__screen--day">
                    <YourDayScreen />
                  </span>
                ) : (
                  <span className="jt-phone__screen">
                    <span className="jt-phone__bar">
                      <span className="jt-phone__title">{screen.title}</span>
                      <span className="jt-phone__badge">{screen.badge}</span>
                    </span>
                    {screen.rows.map((row) => (
                      <span key={row.name} className="jt-phone__row">
                        <span className="jt-phone__rowtop">
                          <span className="jt-phone__name">{row.name}</span>
                          {row.chip !== undefined ? (
                            <span className={`jt-chip jt-chip--${row.chipTone ?? 'violet'}`}>{row.chip}</span>
                          ) : null}
                        </span>
                        <span className="jt-phone__meta">{row.meta}</span>
                        {row.pct !== undefined ? (
                          <span className="jt-phone__bararea">
                            <span className="jt-phone__barfill" style={{ width: row.pct }} />
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        <div className="jt-superapp__dots" role="tablist" aria-label="App screens">
          {appScreens.map((screen, i) => (
            <button
              key={screen.key}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={screen.featureName}
              className={`jt-dot ${i === index ? 'jt-dot--active' : ''}`}
              onClick={() => select(i)}
            />
          ))}
        </div>
      </Reveal>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Grow with Just Treated
// ---------------------------------------------------------------------------

export function JTGrowSection(): JSX.Element {
  const c = useHomepageContent();
  const rows = c.ordered(growSlots, (slot) => `grow.${slot.slug}.order`);

  return (
    <section className="jt-grow" aria-labelledby="jt-grow-title">
      <div className="jt-grow__inner">
        <Reveal className="jt-grow__head">
          <h2 className="jt-h2 jt-h2--large" id="jt-grow-title">
            {c.text('grow.title')}
          </h2>
          <p className="jt-lede jt-lede--start">{c.text('grow.lede')}</p>
        </Reveal>

        <ul className="jt-grow__rows">
          {rows.map((slot, index) => {
            const k = `grow.${slot.slug}`;
            const title = c.text(`${k}.title`);
            return (
              <Reveal
                as="li"
                key={slot.slug}
                // The rows zig-zag by where they sit, so reordering keeps the rhythm.
                className={`jt-growrow ${index % 2 === 1 ? 'jt-growrow--reversed' : ''}`}
              >
                <img
                  className={`jt-growrow__media jt-growrow__media--${slot.tint}`}
                  src={c.image(`${k}.image`, slot.image).src}
                  alt={`${title} — Just Treated partners`}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: slot.focus }}
                />
                <div className="jt-growrow__body">
                  <span className="jt-growrow__kicker">{c.text(`${k}.kicker`)}</span>
                  <h3 className="jt-growrow__title">{title}</h3>
                  <p className="jt-growrow__copy">{c.text(`${k}.copy`)}</p>
                  <CmsAction
                    href={c.href(`${k}.link`)}
                    className="jt-button jt-button--violet jt-button--rounded jt-growrow__cta"
                    analyticsKey={slot.analyticsKey}
                  >
                    {c.text(`${k}.cta`)}
                  </CmsAction>
                </div>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Referral + the parallax band
// ---------------------------------------------------------------------------

export function JTReferSection(): JSX.Element {
  const c = useHomepageContent();
  const bandRef = useRef<HTMLDivElement | null>(null);
  const offset = useParallax(bandRef);
  const band = c.image('refer.image', '');

  return (
    <>
      <section className="jt-refer" aria-labelledby="jt-refer-title">
        <Reveal as="h2" className="jt-h2 jt-h2--xl">
          <span id="jt-refer-title">{c.text('refer.title')}</span>
        </Reveal>
        <p className="jt-lede">{c.text('refer.lede')}</p>
        <CmsAction
          href={c.href('refer.link')}
          className="jt-button jt-button--ink jt-button--rounded"
          analyticsKey="refer_business"
        >
          {c.text('refer.cta')}
        </CmsAction>
      </section>

      <div className="jt-sticky jt-sticky--band">
        <Reveal variant="zoom" className="jt-band">
          <div ref={bandRef} className="jt-band__frame">
            {/* Moves at 0.12× the scroll, against it, and not at all under reduced motion. */}
            <img
              className="jt-band__media"
              src={band.src !== '' ? band.src : '/home/band/delivery-bag-1920.webp'}
              srcSet={
                band.src !== ''
                  ? undefined
                  : '/home/band/delivery-bag-1200.webp 1200w, /home/band/delivery-bag-1920.webp 1683w'
              }
              sizes="100vw"
              alt="A Just Treated delivery bag secured with a seatbelt on a car seat"
              loading="lazy"
              decoding="async"
              style={{ transform: `translateY(${offset.toFixed(1)}px)` }}
            />
          </div>
        </Reveal>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Download our apps
// ---------------------------------------------------------------------------

export function JTDownloadSection({ overlaps = true }: StackProps): JSX.Element {
  const c = useHomepageContent();
  // A deliberate choice between two audiences, so no rotation here.
  const [index, setIndex] = useState(0);
  const active = downloadSlots[index] ?? downloadSlots[0];

  if (active === undefined) return <></>;

  return (
    <section
      id="download"
      className={`jt-download ${overlaps ? '' : 'jt-download--lead'}`}
      aria-labelledby="jt-download-title"
    >
      <div className="jt-download__head">
        <Reveal as="h2" className="jt-h2 jt-h2--large jt-h2--onDark">
          <span id="jt-download-title">{c.text('download.title')}</span>
        </Reveal>
        <p className="jt-lede jt-lede--onDark">{c.text('download.lede')}</p>
      </div>

      <Reveal className="jt-download__body">
        {/* Both photographs are stacked; the tab cross-fades between them over .4s. */}
        <div className="jt-download__media">
          {downloadSlots.map((slot, i) => (
            <img
              key={slot.slug}
              className={`jt-download__img ${i === index ? 'jt-download__img--on' : ''}`}
              src={c.image(slot.imageKey, slot.image).src}
              alt={i === index ? `The Just Treated ${c.text(`download.${slot.slug}.tab`)} app in use` : ''}
              aria-hidden={i !== index}
              loading="lazy"
              decoding="async"
              style={{ objectPosition: slot.focus }}
            />
          ))}
        </div>

        <div
          className="jt-download__copy"
          id="jt-download-panel"
          role="tabpanel"
          aria-label={c.text(`download.${active.slug}.tab`)}
        >
          <div className="jt-download__tabs" role="tablist" aria-label="Choose an app">
            {downloadSlots.map((slot, i) => (
              <button
                key={slot.slug}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-controls="jt-download-panel"
                className={`jt-download__tab ${i === index ? 'jt-download__tab--active' : ''}`}
                onClick={() => setIndex(i)}
              >
                {c.text(`download.${slot.slug}.tab`)}
              </button>
            ))}
          </div>

          <h3 className="jt-download__title">{c.text(`download.${active.slug}.title`)}</h3>
          <p className="jt-download__lede">{c.text(`download.${active.slug}.copy`)}</p>
          <CmsAction
            href={c.href(`download.${active.slug}.link`)}
            className="jt-button jt-button--violet jt-button--rounded"
            analyticsKey={`download_${active.slug}`}
          >
            {c.text(`download.${active.slug}.cta`)}
          </CmsAction>
        </div>
      </Reveal>
    </section>
  );
}

// ---------------------------------------------------------------------------
// News
// ---------------------------------------------------------------------------

/** A headline that links when it has somewhere to go, and is plain text when it does not. */
function Headline({ href, children }: { readonly href: string | undefined; readonly children: string }) {
  return href === undefined ? <>{children}</> : <a href={href}>{children}</a>;
}

export function JTNewsSection(): JSX.Element {
  const t = useT();
  const c = useHomepageContent();
  // The heading is translated chrome until an editor overrides it.
  const edited = useContentOverride();
  const items = c.ordered(newsSlots, (slot) => `news.${slot.slug}.order`);
  const featuredTitle = c.text('news.featured-title');

  return (
    <section className="jt-section jt-news" aria-labelledby="jt-news-title">
      {/*
        The shared container, so the heading starts on the same line as the
        header's wordmark and the Solutions and Grow headings above.
      */}
      <PageContainer>
        <div className="jt-news__head">
          <Reveal as="h2" className="jt-h2 jt-h2--small">
            <span id="jt-news-title">{edited('news.title') ?? t('news.title')}</span>
          </Reveal>
          <DestinationLink destination={routes.news} className="jt-pilllink">
            {t('common.viewAll')} <span aria-hidden="true">→</span>
          </DestinationLink>
        </div>

        <Reveal className="jt-news__grid">
          <article className="jt-feature">
            <img
              className="jt-feature__media"
              src={c.image('news.featured-image', newsFeatured.image).src}
              alt=""
              loading="lazy"
              decoding="async"
            />
            <div className="jt-feature__text">
              <p className="jt-feature__tag">
                {c.text('news.featured-tag')} · {c.text('news.featured-date')}
              </p>
              <h3 className="jt-feature__title">
                <Headline href={c.href('news.featured-link')}>{featuredTitle}</Headline>
              </h3>
            </div>
          </article>

          <ul className="jt-news__list">
            {items.map((slot) => (
              <li key={slot.slug} className="jt-newsitem">
                <img
                  className="jt-newsitem__thumb"
                  src={c.image(`news.${slot.slug}.image`, slot.image).src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: slot.focus }}
                />
                <div className="jt-newsitem__text">
                  <p className="jt-newsitem__tag">{c.text(`news.${slot.slug}.tag`)}</p>
                  <h4 className="jt-newsitem__title">
                    <Headline href={c.href(`news.${slot.slug}.link`)}>
                      {c.text(`news.${slot.slug}.title`)}
                    </Headline>
                  </h4>
                  <p className="jt-newsitem__date">{c.text(`news.${slot.slug}.date`)}</p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      </PageContainer>
    </section>
  );
}
